import React, { useState, useRef } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { Button } from '@redpanda/ui';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import {
  ClipboardDocumentIcon,
  QrCodeIcon,
  ArrowDownTrayIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

interface BookingLinkShareProps {
  storeId: string;
  storeName?: string;
}

const BookingLinkShare: React.FC<BookingLinkShareProps> = ({ storeId, storeName }) => {
  const { t } = useTranslation(['pages', 'translation']);
  const [showQRCode, setShowQRCode] = useState(false);
  const qrCodeRef = useRef<HTMLDivElement>(null);

  // 生成訂位連結
  const bookingUrl = `${window.location.origin}/book/${storeId}`;

  // 複製連結到剪貼簿
  const copyToClipboard = () => {
    navigator.clipboard.writeText(bookingUrl)
      .then(() => {
        toast.success(t('pages:admin.bookingLink.linkCopied'));
      })
      .catch(() => {
        toast.error(t('pages:admin.bookingLink.copyFailed'));
      });
  };

  // 下載 QR Code 圖片
  const downloadQRCode = () => {
    const canvas = qrCodeRef.current?.querySelector('canvas');
    if (!canvas) {
      toast.error(t('pages:admin.bookingLink.downloadFailed'));
      return;
    }

    // 轉換為圖片並下載
    canvas.toBlob((blob) => {
      if (!blob) {
        toast.error(t('pages:admin.bookingLink.generateFailed'));
        return;
      }

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${storeName || 'store'}-booking-qrcode.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success(t('pages:admin.bookingLink.downloaded'));
    });
  };

  return (
    <div className="bg-white dark:bg-slate-800 shadow rounded-lg p-4 md:p-6">
      <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
        {t('pages:admin.bookingLink.shareTitle')}
      </h2>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
        {t('pages:admin.bookingLink.shareDescription')}
      </p>

      {/* 訂位連結 */}
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('pages:admin.bookingLink.linkLabel')}
          </label>
          <div className="flex flex-col md:flex-row gap-2">
            <div className="flex-1 min-w-0 bg-gray-50 dark:bg-slate-900 border border-gray-300 dark:border-slate-600 rounded-md px-4 py-2 font-mono text-sm text-gray-700 dark:text-gray-300 break-all">
              {bookingUrl}
            </div>
            <Button
              type="button"
              onClick={copyToClipboard}
              className="bg-brand-600 hover:bg-brand-700 text-white font-bold py-2 px-4 rounded transition-all duration-200 flex items-center gap-2 whitespace-nowrap"
            >
              <ClipboardDocumentIcon className="h-5 w-5" />
              {t('pages:admin.bookingLink.copyLink')}
            </Button>
          </div>
        </div>

        {/* QR Code 按鈕 */}
        <div className="flex gap-3">
          <Button
            type="button"
            onClick={() => setShowQRCode(!showQRCode)}
            variant="secondary"
            className="flex items-center gap-2"
          >
            <QrCodeIcon className="h-5 w-5" />
            {showQRCode ? t('pages:admin.bookingLink.hideQRCode') : t('pages:admin.bookingLink.showQRCode')}
          </Button>

          {showQRCode && (
            <Button
              type="button"
              onClick={downloadQRCode}
              variant="secondary"
              className="flex items-center gap-2"
            >
              <ArrowDownTrayIcon className="h-5 w-5" />
              {t('pages:admin.bookingLink.downloadQRCode')}
            </Button>
          )}
        </div>

        {/* QR Code 顯示 */}
        {showQRCode && (
          <div className="bg-gray-50 dark:bg-slate-900 border border-gray-300 dark:border-slate-600 rounded-lg p-6">
            <div className="flex flex-col items-center gap-4">
              <div ref={qrCodeRef} className="bg-white p-4 rounded-lg shadow-sm">
                <QRCodeCanvas
                  value={bookingUrl}
                  size={256}
                  level="H"
                  includeMargin={true}
                />
              </div>
              <div className="text-center">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('pages:admin.bookingLink.scanQRCode')}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {t('pages:admin.bookingLink.scanDescription')}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* 使用說明 */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <h3 className="text-sm font-medium text-blue-900 dark:text-blue-300 mb-2">
            {t('pages:admin.bookingLink.usageTipsTitle')}
          </h3>
          <ul className="text-sm text-blue-800 dark:text-blue-400 space-y-1 list-disc list-inside">
            <li>{t('pages:admin.bookingLink.tip1')}</li>
            <li>{t('pages:admin.bookingLink.tip2')}</li>
            <li>{t('pages:admin.bookingLink.tip3')}</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default BookingLinkShare;
